import { Autocomplete, CircularProgress, TextField, } from '@mui/material'
import React, { useEffect, useState } from 'react'

interface SearchDropdownProps<T> {
    fetchOptions: (query: string, reset?: boolean) => Promise<void>; // Function to fetch options
    options: T[],
    clearOptions: () => void
    label: string; // Label for the TextField
    isOptionEqualToValue: (option: T, value: T) => boolean; // Function to compare options
    renderOption: (props: React.HTMLProps<HTMLLIElement>, option: T) => React.ReactNode; // Function to render each option
    onChange?: (value: T | null) => void;
    hasMore?: boolean;
}



const SearchDropdown = <T,>({ fetchOptions, label, isOptionEqualToValue, renderOption, options, clearOptions, onChange, hasMore = true }: SearchDropdownProps<T>) => {
    const [open, setOpen] = useState(false);
    const [loading, setLoading] = useState(false);
    const [searchQuery, setSearchQuery] = useState("");
    const [timeoutId, setTimeoutId] = useState<NodeJS.Timeout | null>(null);

    React.useEffect(() => {

        if (!open) {
            clearOptions();
            return;
        }

        if (timeoutId) {
            clearTimeout(timeoutId);
        }


        if (searchQuery == "" || searchQuery.length > 2) {
            setLoading(true);
            const newTimeoutId = setTimeout(() => {

                fetchOptions(searchQuery, true)
                    .then(() => {
                        setLoading(false);
                    })
                    .catch(() => {
                        clearOptions();
                        setLoading(false);
                    });
            }, 1000);

            setTimeoutId(newTimeoutId);
        } else {
            clearOptions();
        }

    }, [open, searchQuery]);

    const handleListScroll = (event: React.UIEvent<HTMLUListElement>) => {
        if (!open || loading || !hasMore) {
            return;
        }

        const list = event.currentTarget;
        const isNearBottom = list.scrollTop + list.clientHeight >= list.scrollHeight - 8;

        if (!isNearBottom) {
            return;
        }

        setLoading(true);
        fetchOptions(searchQuery, false)
            .then(() => setLoading(false))
            .catch(() => setLoading(false));
    };



    return (
        <Autocomplete
            options={options}
            loading={loading}
            open={open}
            getOptionLabel={(option: any) => option.name || ""}
            onOpen={() => setOpen(true)}
            onClose={() => setOpen(false)}
            isOptionEqualToValue={isOptionEqualToValue}
            renderOption={renderOption}
            fullWidth={true}
            filterOptions={x => x}
            sx={
                { minWidth: "300px" }
            }
            ListboxProps={{
                onScroll: handleListScroll,
            }}
            onInputChange={(_, newValue) => setSearchQuery(newValue)}
            onChange={(_, newValue: T | null) => {
                if (onChange) {
                    onChange(newValue);
                }
            }}
            renderInput={(params) => (
                <TextField
                    {...params}
                    label={label}
                    variant='outlined'
                    InputProps={{
                        ...params.InputProps,
                        endAdornment: (
                            <>
                                {loading ? <CircularProgress color="inherit" size={20} /> : null}
                                {params.InputProps.endAdornment}
                            </>
                        ),
                    }}
                />
            )}
        />
    );
};

export default SearchDropdown;
